import { Mail, MessageCircle, Phone, MapPin, UserCircle2, Info } from "lucide-react";

const TEMAS = [
  "Registro Legal",
  "Solicitud de servicios",
  "Consulta y seguimiento de solicitudes",
  "Telecomunicaciones",
  "Eficiencia Energética",
  "Electrónico",
  "Eléctrica",
  "Esquemas de certificación",
  "Proceso de quejas",
  "Orientación general del portal",
];

const RECOMENDACIONES = [
  "Nombre o razón social",
  "RFC",
  "Correo registrado en el sistema",
  "Sección del portal relacionada con la duda",
  "Breve descripción del tema o incidencia",
];

export function AyudaAsistenciaTab() {
  return (
    <div className="space-y-4">
      <section className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <div className="text-xs font-medium uppercase tracking-wide text-primary-strong">
          SICEN · Apoyo a usuarios
        </div>
        <h2 className="mt-1 text-base font-semibold text-fg">Asistencia personalizada</h2>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Este apartado concentra la atención inicial para usuarios del sistema SICEN.
          Aquí puedes ubicar el canal principal de apoyo para resolver dudas, solicitar
          orientación y canalizar consultas relacionadas con Registro Legal, solicitudes,
          seguimiento y otras secciones del portal.
        </p>
      </section>

      <section id="que-es" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          1. ¿Qué es la asistencia personalizada?
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Es el canal de apoyo definido para orientar a los usuarios del portal SICEN
          cuando tienen dudas sobre su acceso, su registro, el uso del sistema o el
          seguimiento de trámites. Desde este punto se brinda atención inicial y, cuando
          corresponde, se canaliza la consulta al área adecuada.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <MiniCard label="Función" value="Orientación inicial al usuario" />
          <MiniCard label="Apoya en" value="Dudas, seguimiento y canalización" />
          <MiniCard label="Cobertura" value="Temas operativos del portal SICEN" />
        </div>
      </section>

      <section id="para-que" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          2. ¿Para qué sirve?
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Sirve como punto de partida para que los usuarios reciban orientación clara,
          puedan resolver dudas operativas y, en caso necesario, ser dirigidos al equipo
          interno correspondiente.
        </p>
      </section>

      <section id="cuando-usarla" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          3. ¿Cuándo utilizar este apoyo?
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          Cuando tengas dudas relacionadas con tu usuario, el registro de la empresa o
          persona, el avance de una solicitud, la documentación requerida o la forma
          correcta de operar una sección dentro del sistema.
        </p>
        <div className="mt-3 flex items-start gap-2 rounded border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          Este espacio facilita la atención al usuario y evita que las consultas se
          dispersen, manteniendo una comunicación más ordenada y un seguimiento más claro.
        </div>
      </section>

      <section id="temas" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          4. Temas que se pueden canalizar
        </h3>
        <p className="mt-2 text-sm text-fg-muted">
          La asistencia personalizada puede ayudar a encaminar consultas relacionadas con:
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {TEMAS.map((t) => (
            <span
              key={t}
              className="rounded-full bg-estado-info-bg px-3 py-1 text-xs font-medium text-estado-info"
            >
              {t}
            </span>
          ))}
        </div>
      </section>

      <section id="contacto" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          5. Contacto principal
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted">
          El contacto principal definido para este apoyo es <span className="font-medium">América
          Muñiz Lara</span>, quien podrá brindar atención inicial y canalizar las dudas al
          área correspondiente dentro del ecosistema de atención del sistema SICEN.
        </p>
        <div className="mt-4 flex flex-col gap-4 rounded-lg border bg-surface-2/40 p-4 sm:flex-row sm:items-start">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-primary-tint text-primary-strong">
            <UserCircle2 className="h-10 w-10" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-fg">América Muñiz Lara</h4>
            <p className="mt-1 text-xs text-fg-muted">
              Contacto principal de apoyo para usuarios del sistema SICEN.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <ContactoCard
                icon={<Mail className="h-4 w-4" />}
                label="Correo electrónico"
                value={<a className="text-primary-strong hover:underline" href="mailto:amuniz@npmx.org">amuniz@npmx.org</a>}
              />
              <ContactoCard
                icon={<MessageCircle className="h-4 w-4" />}
                label="WhatsApp"
                value={
                  <a
                    className="text-primary-strong hover:underline"
                    href="https://wa.me/5215617288722"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +52 1 56 1728 8722
                  </a>
                }
              />
              <ContactoCard
                icon={<Mail className="h-4 w-4" />}
                label="Canal institucional"
                value={<a className="text-primary-strong hover:underline" href="mailto:contacto@npmx.org">contacto@npmx.org</a>}
              />
              <ContactoCard
                icon={<Phone className="h-4 w-4" />}
                label="Teléfono"
                value={<a className="text-primary-strong hover:underline" href="tel:+525515205386">(55) 1520-5386</a>}
              />
            </div>
          </div>
        </div>
      </section>

      <section id="datos-npmx" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          6. Datos institucionales
        </h3>
        <p className="mt-2 text-sm text-fg-muted">
          Además del contacto principal, puedes identificar los datos institucionales
          generales de NPMX:
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <ContactoCard
            icon={<Mail className="h-4 w-4" />}
            label="Correo institucional"
            value={<a className="text-primary-strong hover:underline" href="mailto:contacto@npmx.org">contacto@npmx.org</a>}
          />
          <ContactoCard
            icon={<Phone className="h-4 w-4" />}
            label="Teléfono"
            value={<a className="text-primary-strong hover:underline" href="tel:+525515205386">(55) 1520-5386</a>}
          />
          <div className="sm:col-span-2">
            <ContactoCard
              icon={<MapPin className="h-4 w-4" />}
              label="Dirección"
              value={<>Alborada 124, Núm. interior 903, Col. Parque del Pedregal, Alcaldía Tlalpan, Ciudad de México, C.P. 14010</>}
            />
          </div>
        </div>
      </section>

      <section id="recomendaciones" className="rounded-lg border-l-4 border-l-primary-strong bg-surface p-5 shadow-card">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          7. Recomendaciones para recibir atención
        </h3>
        <p className="mt-2 text-sm text-fg-muted">
          Para agilizar la respuesta, procura compartir desde el inicio la información más
          importante sobre tu caso:
        </p>
        <ul className="mt-3 grid gap-1 sm:grid-cols-2">
          {RECOMENDACIONES.map((x) => (
            <li key={x} className="flex items-center gap-2 text-sm text-fg-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {x}
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-start gap-2 rounded border border-estado-info/25 bg-estado-info-bg p-3 text-xs text-estado-info">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          Mientras más claro sea el contexto que compartas, más rápido podrá orientarse tu
          solicitud o dirigirse al área adecuada.
        </div>
      </section>
    </div>
  );
}

function MiniCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-surface-2/40 p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-fg-subtle">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-fg">{value}</div>
    </div>
  );
}

function ContactoCard({
  icon, label, value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded border bg-surface p-3 shadow-sm">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-fg-subtle">
        <span className="text-fg-faint">{icon}</span> {label}
      </div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
